// types
import { Grid, Typography, TypographyProps } from '@mui/material';
import {
	Account,
	Act,
	Benefit,
	CashInMethod,
	Category,
	Component,
	Contract,
	Entity,
	ExpenseType,
	Invoice,
	Loan,
	Payment,
	TransactionType,
	WithdrawalApplication,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// ui
import { BlockchainHash } from '../BlockchainHash/BlockchainHash';
import { IconCircle, IconCircleProps, transactionIconTypes } from '../IconCircle/IconCircle';
import { IconCircleLoan } from '../IconCircleLoan/IconCircleLoan';
import { TableInfo, TableInfoProps } from '../TableInfo/TableInfo';

// utils
import { formatCurrency } from '../../../utils/currency';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface TableInfoTransactionLineItem {
	IconCircleProps: IconCircleProps;
	label: TypographyProps;
	value: TypographyProps;
}

export interface TableInfoTransactionProps {
	account?: Account | null | undefined;
	acts?: Act[];
	benefit?: Benefit | null | undefined;
	cashInMethod?: CashInMethod | null | undefined;
	category?: Category | null | undefined;
	component?: Component | null | undefined;
	contract?: Contract | null | undefined;
	date?: string | null | undefined;
	designatedAccount?: Account | null | undefined;
	expenseType?: ExpenseType | null | undefined;
	fromAccount?: Account | null | undefined;
	fromEntity?: Entity | null | undefined;
	invoice?: Invoice | null | undefined;
	lineItems?: TableInfoTransactionLineItem[];
	loan?: Loan | null | undefined;
	payments?: Payment[];
	quantity?: number | null | undefined;
	toAccount?: Account | null | undefined;
	toEntity?: Entity | null | undefined;
	withdrawalApplication?: WithdrawalApplication | null | undefined;
	total?: {
		amount: number;
		currency: string | undefined;
	};
	totalReceived?: {
		amount: number;
		currency: string | undefined;
	};
	type?: TransactionType | null | undefined;
}

export const TableInfoTransaction = ({
	account,
	acts = [],
	benefit,
	category,
	component,
	cashInMethod,
	contract,
	date,
	designatedAccount,
	expenseType,
	fromAccount,
	fromEntity,
	invoice,
	lineItems = [],
	loan,
	payments = [],
	quantity,
	toAccount,
	toEntity,
	withdrawalApplication,
	total,
	totalReceived,
	type,
}: TableInfoTransactionProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const dataInfo: TableInfoProps['data'] = [];

	if (account) {
		const IconCircleProps: IconCircleProps = {
			type: 'account',
		};

		if (account.type === 'LOCAL') {
			IconCircleProps.type = 'local';
		}

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('account')}</Typography>
					</Grid>
				</Grid>
			),
			value: account.name,
		});
	}

	if (cashInMethod) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="cashInMethod" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('cashInMethod')}</Typography>
					</Grid>
				</Grid>
			),
			value: cashInMethod.name,
		});
	}

	if (component) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="component" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('component')}</Typography>
					</Grid>
				</Grid>
			),
			value: component.name,
		});
	}

	if (category) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="category" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('category')}</Typography>
					</Grid>
				</Grid>
			),
			value: category.name,
		});
	}

	if (expenseType) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="expenseType" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('expenseType')}</Typography>
					</Grid>
				</Grid>
			),
			value: expenseType.name,
		});
	}

	if (acts.length && featureFlags['ACTIVITIES']) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="act" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('activities')}</Typography>
					</Grid>
				</Grid>
			),
			value: acts.map((act) => act.name).join(', '),
		});
	}

	if (date) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="date" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('date')}</Typography>
					</Grid>
				</Grid>
			),
			value: date,
		});
	}

	if (fromEntity) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="entity" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{type === 'OPEX' ? t('entity') : t('from')}</Typography>
					</Grid>
				</Grid>
			),
			value: fromEntity.name,
		});
	}

	if (loan) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircleLoan loan={loan} size="sm" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('loan')}</Typography>
					</Grid>
				</Grid>
			),
			value: loan.code || t('na'),
		});
	}

	if (toEntity) {
		const IconCircleProps: IconCircleProps = {
			type: 'supplier',
		};

		if (type === 'CASH_BENEFIT') {
			IconCircleProps.type = 'beneficiary';

			if (toEntity.subtype === 'ORGANIZATION') {
				IconCircleProps.type = 'organization';
			}
		} else if (type === 'OPEX') {
			IconCircleProps.type = 'entity';
		}

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('to')}</Typography>
					</Grid>
				</Grid>
			),
			value: toEntity.name,
		});
	}

	if (contract) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="contract" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('contract')}</Typography>
					</Grid>
				</Grid>
			),
			value: contract.num,
		});
	}

	if (invoice) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="invoice" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('invoice')}</Typography>
					</Grid>
				</Grid>
			),
			value: invoice.num,
		});
	}

	if (fromAccount) {
		const IconCircleProps: IconCircleProps = {
			type: 'account',
		};

		if (fromAccount.type === 'LOCAL') {
			IconCircleProps.type = 'local';
		}

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>
							{type === 'TRANSFER' ? t('fromAccount') : t('account')}
						</Typography>
					</Grid>
				</Grid>
			),
			value: fromAccount.name,
		});
	}

	if (designatedAccount) {
		const IconCircleProps: IconCircleProps = {
			type: 'account',
		};

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('designatedAccount')}</Typography>
					</Grid>
				</Grid>
			),
			value: designatedAccount.name,
		});
	}

	if (toAccount) {
		const IconCircleProps: IconCircleProps = {
			type: 'account',
		};

		if (toAccount.type === 'LOCAL') {
			IconCircleProps.type = 'local';
		}

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>
							{type === 'TRANSFER' ? t('toAccount') : t('to')}
						</Typography>
					</Grid>
				</Grid>
			),
			value: toAccount.name,
		});
	}

	if (withdrawalApplication?.idWbg) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="withdrawalApplication" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('withdrawalApplication')}</Typography>
					</Grid>
				</Grid>
			),
			value: withdrawalApplication.idWbg,
		});
	}

	for (let i = 0; i < payments.length; i++) {
		const payment = payments[i];
		const loan = payment.loan as Loan;

		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircleLoan loan={loan} size="sm" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{loan.code || t('na')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid alignItems="center" justifyContent="flex-end" container spacing={1}>
					{payment.blkTransaction.id && (
						<Grid item>
							<BlockchainHash hash={payment.blkTransaction.id} />
						</Grid>
					)}
					<Grid item>
						<Typography color="brand.grey.500">
							{formatCurrency({
								currency: payment.currency,
								value: Number(payment.amount) + Number(payment.tax || 0),
							})}
						</Typography>
					</Grid>
				</Grid>
			),
		});
	}

	if (benefit && quantity) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="benefit" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('benefit')}</Typography>
					</Grid>
				</Grid>
			),
			value: `${quantity} ${benefit.name}`,
		});
	}

	if (total && type) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type={transactionIconTypes[type]} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('total')}</Typography>
					</Grid>
				</Grid>
			),
			value: formatCurrency({
				currency: total.currency,
				value: total.amount,
			}),
		});
	}

	if (totalReceived && type) {
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type={transactionIconTypes[type]} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('totalReceived')}</Typography>
					</Grid>
				</Grid>
			),
			value: totalReceived.amount
				? formatCurrency({
						currency: totalReceived.currency,
						value: totalReceived.amount,
					})
				: t('na'),
		});
	}

	for (let i = 0; i < lineItems.length; i++) {
		const { IconCircleProps, label, value } = lineItems[i];
		dataInfo.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" {...IconCircleProps} />
					</Grid>
					<Grid item>
						<Typography fontWeight={500} {...label} />
					</Grid>
				</Grid>
			),
			value: <Typography {...value} />,
		});
	}

	return <TableInfo data={dataInfo} />;
};
